import { useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { Tenant } from '@mg/shared';
import { PageHeader } from '../components/shell/PageHeader';
import { menuIcons } from '../components/shell/menuIcons';
import { FooterSaveProvider } from '../components/settings/FooterSaveProvider';
import { StickySaveFooter } from '../components/settings/StickySaveFooter';
import { Card, CardBody } from '../components/ui/Card';
import { EmptyState } from '../components/ui/States';
import { useTenant } from '../auth/TenantContext';
import { useCanEditWorkspace } from '../auth/useWorkspaceAdmin';
import { MEMBER_READ_ONLY_NOTICE } from '../features/brand/brandCopy';
import { editPagePath, isEditPageKey, type EditPageKey } from '../features/brand/paths';
import { BrandAssetsPage } from '../features/brand-edit/assets/BrandAssetsPage';
import { BrandInformationPage } from '../features/brand-edit/brand/BrandInformationPage';
import { DangerZonePage } from '../features/brand-edit/danger/DangerZonePage';
import { EditPageGuard } from '../features/brand-edit/EditPageGuard';
import { useEditPageScroll } from '../features/brand-edit/useEditPageScroll';
import { WorkflowPage } from '../features/brand-edit/workflow/WorkflowPage';
import { WorkspaceSideMenu } from '../features/brand-edit/WorkspaceSideMenu';
import { TestModeBanner } from '../features/testMode/TestModeBanner';

const TITLE = 'Edit Brand Workspace';
const DESCRIPTION = "Update your brand's details, brand assets and workflow.";

/**
 * Edit Brand Workspace with Brand Workspace v2 (spec:159-200; web.md §6): `/workspace/edit/:page`,
 * one of Brand information, Brand assets and style, Workflow and Danger zone, for the active workspace.
 * Today's page (WorkspaceFormPage) is untouched and still serves the install while the flag is off.
 *
 * - The side menu on the left lists the pages and, under the open one, its sections; below `lg` it is
 *   a row above the content.
 * - Each page has one Save, in the sticky footer at the bottom of its content (none on Danger zone,
 *   none for a member), and one guard that asks before its unsaved changes are lost.
 * - A member sees every page read-only, with a notice saying so, and no Danger zone (E30).
 * - An unknown page, or Danger zone for a member, goes to Brand information.
 */
export function WorkspaceEditPage() {
  const { page: param } = useParams<{ page: string }>();
  const { activeTenant } = useTenant();
  const canEdit = useCanEditWorkspace(activeTenant?.id);

  if (!activeTenant) {
    return (
      <>
        <PageHeader title={TITLE} icon={menuIcons.workspaceEdit} />
        <Card>
          <CardBody>
            <EmptyState title="No active workspace" description="Select or create a Brand Workspace first." />
          </CardBody>
        </Card>
      </>
    );
  }
  if (!isEditPageKey(param) || (param === 'danger' && !canEdit)) {
    return <Navigate to={editPagePath('brand', activeTenant.slug)} replace />;
  }
  return <EditPages page={param} workspace={activeTenant} canEdit={canEdit} />;
}

function EditPages({ page, workspace, canEdit }: { page: EditPageKey; workspace: Tenant; canEdit: boolean }) {
  const layoutRef = useRef<HTMLDivElement>(null);
  useEditPageScroll(layoutRef, page, workspace.id);
  // A member saves nothing: no footer, and nothing registers with it.
  const hasFooter = canEdit && page !== 'danger';

  return (
    <>
      <PageHeader title={TITLE} icon={menuIcons.workspaceEdit} description={DESCRIPTION} />
      <TestModeBanner />
      {!canEdit && (
        <p className="mb-4 rounded-lg border border-line bg-page px-4 py-3 text-sm text-ink-label">
          {MEMBER_READ_ONLY_NOTICE}
        </p>
      )}
      {/* The settings pages' box (`data-settings-tabs`): StandardLayout lets it grow to the bottom of
          the scroll region, so on a short page the footer still rests at the bottom of the content area.
          The menu keeps its own height (sticky); the content column grows. */}
      <div ref={layoutRef} data-settings-tabs="" className="flex grow flex-col gap-4 lg:flex-row lg:gap-6">
        <WorkspaceSideMenu page={page} slug={workspace.slug} canEdit={canEdit} />
        {/* Keyed: a new page or workspace starts from its saved values, with a fresh footer. */}
        <div key={`${workspace.id}:${page}`} className="flex min-w-0 grow flex-col lg:flex-1">
          <FooterSaveProvider active={hasFooter} scope={page}>
            {/* Before the content: its blocker updates before any of the content's effects navigate. */}
            <EditPageGuard />
            <div className="grow space-y-4">
              <PageContent page={page} workspace={workspace} canEdit={canEdit} />
            </div>
            {hasFooter && <StickySaveFooter />}
          </FooterSaveProvider>
        </div>
      </div>
    </>
  );
}

function PageContent({ page, workspace, canEdit }: { page: EditPageKey; workspace: Tenant; canEdit: boolean }) {
  switch (page) {
    case 'brand':
      return <BrandInformationPage workspace={workspace} canEdit={canEdit} />;
    case 'assets':
      return <BrandAssetsPage workspace={workspace} canEdit={canEdit} />;
    case 'workflow':
      return <WorkflowPage workspace={workspace} canEdit={canEdit} />;
    case 'danger':
      return <DangerZonePage workspace={workspace} canEdit={canEdit} />;
  }
}
